import React from "react";
import { ExternalLink } from "lucide-react";
import { detectVideoEmbed } from "../lib/video";

/**
 * VideoEmbed — renders a video URL as YouTube / Vimeo iframe or HTML5 <video>.
 *
 * Props:
 *  - url: string | null
 *  - className: extra classes for the wrapper (defaults to "w-full h-full")
 *  - videoProps: extra props for HTML5 <video> (e.g. controls, controlsList)
 *  - allowDownload: if false (default), passes controlsList="nodownload" on file
 *  - testid: data-testid for the inner playable element
 *
 * Caller is responsible for wrapping in an `aspect-video` (or sized) container.
 */
export default function VideoEmbed({
  url,
  className = "w-full h-full",
  videoProps = {},
  testid = "video-embed",
}) {
  const { provider, embed_url } = detectVideoEmbed(url);

  if (provider === "youtube" || provider === "vimeo") {
    return (
      <iframe
        src={embed_url}
        className={className}
        title="Lesson video"
        frameBorder="0"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        data-testid={testid}
        data-provider={provider}
      />
    );
  }

  if (provider === "file") {
    return (
      <video
        src={embed_url}
        controls
        preload="metadata"
        controlsList="nodownload noremoteplayback"
        className={className}
        data-testid={testid}
        data-provider="file"
        {...videoProps}
      >
        Your browser does not support video playback.
      </video>
    );
  }

  // Unrecognised — show clean fallback
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 bg-black/70 text-white/70 p-6 text-center ${className}`}
      data-testid={`${testid}-fallback`}
    >
      <p className="text-sm font-bold">Unable to embed this video.</p>
      <p className="text-[11px] text-white/55">
        Paste a YouTube or Vimeo URL, or a direct .mp4 / .webm / .mov link.
      </p>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-fc-red text-[11px] inline-flex items-center gap-1 hover:underline break-all max-w-full"
        >
          <ExternalLink size={11} /> {url}
        </a>
      )}
    </div>
  );
}
